import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { useTimeZone, useUserData } from '../../../hooks'
import { IconSpinner } from '../../Icon'
import Text from '../../Text'
import { useChatroomMessagesScroll } from '../Chatroom.hooks'
import { formatChatroomDateDivider } from '../Chatroom.utils'
import { ChatroomDateDivider } from '../ChatroomDateDivider'
import { ChatroomMessage } from '../ChatroomMessage'
import { ChatroomMessageDeleted } from '../ChatroomMessageDeleted'
import { ChatroomMessageEditForm } from '../ChatroomMessageEditForm'
import { ChatroomMessagesSkeleton } from '../ChatroomMessagesSkeleton'
import { ChatroomMessagesUnreadDivider } from '../ChatroomMessagesUnreadDivider'
import { ChatroomStartedLine } from '../ChatroomStartedLine'
import { ChatroomSystemMessage } from '../ChatroomSystemMessage'

import {
  getChatroomMessagesUnreadDividerIndex,
  getChatroomShouldShowDateDivider,
  isChatroomMessageEditing,
} from './ChatroomMessages.utils'
import { ChatroomMessagesEmpty } from './ChatroomMessagesEmpty'

import * as styles from './ChatroomMessages.module.css'

function ChatroomMessages({
  EmptyState = ChatroomMessagesEmpty,
  chatroomCreated,
  editingMessageUuid,
  filePreviewProps,
  hasMoreOlder,
  isFullScreen,
  isInitialReady,
  isLoadingOlder,
  isPrivate,
  messageDraftRef,
  messages,
  outboundUuid,
  unreadAnchorMessageUuid,
  onEditMessage,
  onFinishEditMessage,
  onLoadOlderPage,
  onMessageSaved,
  onReadBottomReached,
  onRefetchVisibleMessages,
  onUpdateMessage,
}) {
  const { t } = useTranslation()

  const currentUser = useUserData()

  const timeZone = useTimeZone()

  const unreadDividerIndex = getChatroomMessagesUnreadDividerIndex({
    messages,
    unreadAnchorMessageUuid,
  })

  const { scrollRef, setTopSentinelElement, unreadDividerRef } =
    useChatroomMessagesScroll({
      currentUserId: currentUser?.id,
      hasMoreOlder,
      isInitialReady,
      isLoadingOlder,
      messages,
      unreadAnchorMessageUuid,
      onLoadOlderPage,
      onReadBottomReached,
    })

  function renderMessage(message) {
    return (
      <ChatroomMessage
        currentUserId={currentUser?.id}
        filePreviewProps={filePreviewProps}
        isPrivate={isPrivate}
        message={message}
        outboundUuid={outboundUuid}
        onEditMessage={onEditMessage}
        onRefetchVisibleMessages={onRefetchVisibleMessages}
        onUpdateMessage={onUpdateMessage}
      />
    )
  }

  return !isInitialReady ? (
    <ChatroomMessagesSkeleton />
  ) : messages.length === 0 ? (
    EmptyState ? (
      <EmptyState />
    ) : null
  ) : (
    <div className={styles.scroll} ref={scrollRef}>
      {hasMoreOlder ? <div ref={setTopSentinelElement} /> : null}
      {!hasMoreOlder && chatroomCreated ? (
        <ChatroomStartedLine createdAt={chatroomCreated} />
      ) : null}
      {isLoadingOlder ? (
        <div className={styles.loadingOlder}>
          <IconSpinner />
          <Text isSecondary>{t('Chatroom.LoadingHistoryKey')}</Text>
        </div>
      ) : null}
      <div className={styles.list}>
        {messages.map((message, index) => (
          <Fragment key={message.uuid}>
            {getChatroomShouldShowDateDivider({
              hasMoreOlder,
              index,
              message,
              previousMessage: messages[index - 1],
              timeZone,
            }) ? (
              <ChatroomDateDivider
                label={formatChatroomDateDivider({
                  date: message.created,
                  t,
                  timeZone,
                })}
              />
            ) : null}
            {index === unreadDividerIndex ? (
              <ChatroomMessagesUnreadDivider elementRef={unreadDividerRef} />
            ) : null}
            {message.deleted ? (
              <ChatroomMessageDeleted message={message} />
            ) : message.is_system ? (
              <ChatroomSystemMessage message={message} />
            ) : isChatroomMessageEditing({ editingMessageUuid, message }) ? (
              <ChatroomMessageEditForm
                isFullScreen={isFullScreen}
                message={message}
                messageDraftRef={messageDraftRef}
                outboundUuid={outboundUuid}
                onFinishEdit={onFinishEditMessage}
                onSaved={onMessageSaved}
                onUpdateMessage={onUpdateMessage}
              >
                {renderMessage(message)}
              </ChatroomMessageEditForm>
            ) : (
              renderMessage(message)
            )}
          </Fragment>
        ))}
      </div>
    </div>
  )
}

export default ChatroomMessages
